'use client';
import * as React from 'react';
import dynamic from 'next/dynamic';
import { BaseSection } from '@/app/base-ui';
import ListActions from './components/ListActions';
import { Box, useTheme, Skeleton } from '@mui/material';
import MainBanner from './components/MainBanner';
import FeaturedGridPosts from './components/FeaturedGridPosts';
import NewestPost from './components/NewestPost';
import MyRoomBanner from './components/MyRoomBanner';
import { Loading } from '@/app/components/Posts/Post/components/Loading';

// Skeleton component for dynamic imports
const PostSkeletonLoader = () => {
    const theme = useTheme();
    return (
        <Box sx={{ backgroundColor: 'white', pt: 10 }}>
            <Box sx={{ paddingX: { mobile: '20px', tablet: '50px' }, mb: 2 }}>
                <Box sx={{ height: '27px' }} /> {/* Placeholder for title */}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: theme.palette.grayscale.gray02 }}>
                {[...Array(2)].map((_, index) => (
                    <Box key={index} sx={{ py: 1 }}>
                        <Loading.Media />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

//dynamic imports for below-the-fold components to reduce initial bundle size
const ChallengesList = dynamic(() => import('./components/ChallengesList'));
const NewsList = dynamic(() => import('./components/NewsList'), {
    loading: () => <PostSkeletonLoader />
});
const NewestProduct = dynamic(() => import('./components/NewestProduct'), {
    loading: () => <PostSkeletonLoader />
});
const HomeStore = dynamic(() => import('./components/HomeStore'), {
    loading: () => <PostSkeletonLoader />
});
const Curious = dynamic(() => import('./components/Curious'), {
    ssr: false
});
const Footer = dynamic(() => import('./components/Footer'));

export default function Banners() {
    const theme = useTheme();
    const ref = React.useRef(null);
    const scrollContainerRef = React.useRef(null);
    const curiousTriggerRef = React.useRef<HTMLDivElement>(null);
    const [showCurious, setShowCurious] = React.useState(false);

    // Replace Framer Motion scroll detection with IntersectionObserver for better performance
    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShowCurious(true);
                    // Disconnect after triggering once to avoid unnecessary observations
                    observer.disconnect();
                }
            },
            {
                threshold: 0.1,
                rootMargin: '100px' // Trigger slightly before element comes into view
            }
        );

        if (curiousTriggerRef.current) {
            observer.observe(curiousTriggerRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <BaseSection
            ref={ref}
            sx={{ borderRadius: '0px', background: theme.palette.background.default, height: '100%', width: '100%', flex: '1 1 auto' }}
        >
            <Box
                className="overflow-auto"
                style={{ display: 'flex', flexDirection: 'column', gap: 8, backgroundColor: theme.palette.grayscale.gray02 }}
                ref={scrollContainerRef}
            >
                <Box sx={{ position: 'relative', mb: { laptop: 18, tablet: 18, mobile: 0 } }}>
                    <MainBanner />
                    <Box
                        sx={{
                            display: { laptop: 'flex', tablet: 'flex', mobile: 'none' },
                            borderRadius: '16px',
                            justifyContent: 'center',
                            zIndex: 99,
                            width: { laptop: 'calc(100% - 100px)', tablet: 'calc(100% - 100px)', mobile: '' },
                            backdropFilter: 'blur(10px)',
                            background: 'rgba(255, 255, 255, 0.90)',
                            position: 'absolute',
                            left: 'calc(50%)',
                            transform: 'translateX(-50%)',
                            bottom: '-50px',
                            boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05)',
                        }}
                    >
                        <ListActions />
                    </Box>
                </Box>

                <Box sx={{ background: theme.palette.background.default, boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05)', display: { tablet: 'none', laptop: 'none', mobile: '' }, mt: -2 }}>
                    <ListActions />
                </Box>
                <FeaturedGridPosts />
                <MyRoomBanner />
                <NewsList />
                <NewestProduct />
                {/* <NewestPost /> */}
                <ChallengesList />
                <HomeStore />
                {/* Intersection observer trigger point */}
                <div ref={curiousTriggerRef} style={{ height: 1 }} />

                {/* <Box sx={{ paddingX: { mobile: '20px', tablet: '50px' } }}> */}
                    {/* check if data qualification > 0 ==> show ? null */}
                    {/* <Box sx={{ mb: 10 }}>
        <ContentSlider /> // hide until the next stage
      </Box> */}
                    {/* check if data qualification > 0 ==> show ? null */}
                    {/* hide for stage 1 */}
                    {/* <Box sx={{ display: 'flex', flexDirection: 'column', mb: 10 }}>
                        <ExpertChannel />
                    </Box> */}
                    {/* <Box sx={{ display: 'flex', flexDirection: 'column', mb: 10 }}>
                        <PopularPosts />
                    </Box> */}
                {/* </Box> */}
            </Box>
            {showCurious && (
                <Box sx={{ paddingX: { mobile: '20px', tablet: '50px' } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 10, pt: 10 }}>
                        <Curious />
                    </Box>
                </Box>
            )}
            <Footer />
        </BaseSection>
    );
}
